import _ from "lodash";
import { binarySearch, distance, interpolate } from "./PlaylistUtils";
import { nearestPointOnLine, parseRoutePositionData } from "./Geometry";

export function calculateDistanceInPlaylist(timestamp, geometry, cameraOffset = 0, debug = false) {
    if (timestamp > 99999999999) {
        timestamp /= 1000;
    }
    const timeToDistanceData = _.get(geometry, ["time_to_distance"], []);

    const timestampRanges = timeToDistanceData.map((segment) => {
        if (!segment.length) {
            return [];
        }
        return [segment[0][0], segment[segment.length - 1][0]];
    });

    const arraySubsectionIndex = _.findLastIndex(timestampRanges, (range) => {
        if (!range.length) {
            return false;
        }
        return range[0] < timestamp;
    });

    if (arraySubsectionIndex < 0) {
        return null;
    }
    const timeToDistanceSubsection = timeToDistanceData[arraySubsectionIndex];

    const distanceIndex = binarySearch(timestamp, timeToDistanceSubsection, (point) => point[0]);

    if (distanceIndex < 0) {
        return null;
    }

    const distanceItem = timeToDistanceSubsection[distanceIndex];

    if (distanceIndex === timeToDistanceSubsection.length - 1) {
        return distanceItem[1];
    }

    const nextDistanceItem = timeToDistanceSubsection[distanceIndex + 1];
    const totalTimeDiff = Math.abs(nextDistanceItem[0] - distanceItem[0]);
    const totalDistanceDiff = nextDistanceItem[1] - distanceItem[1];

    const interpolatedTimeDiff = timestamp - distanceItem[0];
    const ratioDiff = interpolatedTimeDiff / totalTimeDiff;
    const interpolatedPlaylistDistance = totalDistanceDiff * ratioDiff + distanceItem[1];

    const offsettedDistance = interpolatedPlaylistDistance + cameraOffset;
    return offsettedDistance;
}

export function distanceToRoutePosition(playlistDistance, routePositionData) {
    const nearestGeomIndex = binarySearch(playlistDistance, routePositionData, (point) => point[0]);

    if (nearestGeomIndex < 0) {
        return;
    }

    const currentGeomItem = routePositionData[nearestGeomIndex];
    if (playlistDistance === currentGeomItem[0] || nearestGeomIndex === routePositionData.length - 1) {
        return {
            routeID: currentGeomItem[1],
            subposition: currentGeomItem[2],
            position: currentGeomItem[3],
        };
    }

    const nextGeomItem = routePositionData[nearestGeomIndex + 1];
    const totalDistanceDiff = nextGeomItem[0] - currentGeomItem[0];
    const totalPosDiff = nextGeomItem[3] - currentGeomItem[3];

    const interpolatedDistanceDiff = playlistDistance - currentGeomItem[0];
    const distanceDiffRatio = interpolatedDistanceDiff / totalDistanceDiff;

    const interpolatedPosition = totalPosDiff * distanceDiffRatio + currentGeomItem[3];

    return {
        routeID: currentGeomItem[1],
        subposition: currentGeomItem[2],
        position: interpolatedPosition,
    };
}

export function distanceToRouteCoordinate(playlistDistance, routeCoordData) {
    if (!routeCoordData) {
        return null;
    }
    const distanceRanges = routeCoordData.map((segment) => {
        return [segment[0][0], segment[segment.length - 1][0]];
    });

    const arraySubsectionIndex = _.findIndex(distanceRanges, (range) => {
        return range[0] <= playlistDistance && playlistDistance <= range[1];
    });

    if (arraySubsectionIndex < 0) {
        return null;
    }
    const distanceCoordinateSubsection = routeCoordData[arraySubsectionIndex];

    const nearestGeomIndex = binarySearch(playlistDistance, distanceCoordinateSubsection, (point) => point[0]);

    if (nearestGeomIndex < 0 || _.isNil(nearestGeomIndex) || !distanceCoordinateSubsection[nearestGeomIndex + 1]) {
        return null;
    }
    const currentGeomItem = distanceCoordinateSubsection[nearestGeomIndex];
    if (playlistDistance === currentGeomItem[0]) {
        return [currentGeomItem[1], currentGeomItem[2]];
    }

    const nextGeomItem = distanceCoordinateSubsection[nearestGeomIndex + 1];
    const totalDistanceDiff = nextGeomItem[0] - currentGeomItem[0];
    const interpolatedDistanceDiff = playlistDistance - currentGeomItem[0];
    const distanceDiffRatio = interpolatedDistanceDiff / totalDistanceDiff;

    const startPos = [currentGeomItem[1], currentGeomItem[2]];
    const endPos = [nextGeomItem[1], nextGeomItem[2]];

    const interpolatedCoordPosition = interpolate(startPos, endPos, distanceDiffRatio);

    return interpolatedCoordPosition;
}

export function getCoordsAtTimestamp(timestamp, geometryData, sourceOffset = 0) {
    const playlistDistance = calculateDistanceInPlaylist(timestamp, geometryData, sourceOffset);

    if (!playlistDistance) {
        return null;
    }

    const routeCoordData = _.get(geometryData, ["distance_to_coordinate"], []);
    return distanceToRouteCoordinate(playlistDistance, routeCoordData);
}

export function getRoutePosAtTimestamp(timestamp, geometryData, routeSystemID, sourceOffset) {
    const playlistDistance = calculateDistanceInPlaylist(timestamp, geometryData, sourceOffset);

    if (!playlistDistance) {
        return null;
    }
    const routeCoordData = _.get(geometryData, ["distance_to_route_position"], []);

    const routePosData = distanceToRoutePosition(playlistDistance, routeCoordData);

    if (!routePosData) {
        return null;
    }
    return parseRoutePositionData(routePosData, routeSystemID);
}

export function playlistDistanceToTimestamp(playlistDistance, geometry) {
    const timeToDistanceData = _.get(geometry, ["time_to_distance"], []);

    const distanceRanges = timeToDistanceData.map((segment) => {
        return [segment[0][1], segment[segment.length - 1][1]];
    });

    const arraySubsectionIndex = _.findIndex(distanceRanges, (range) => {
        return range[0] < playlistDistance && playlistDistance < range[1];
    });
    if (arraySubsectionIndex < 0) {
        return null;
    }
    const timeToDistanceSubsection = timeToDistanceData[arraySubsectionIndex];

    const distanceIndex = binarySearch(playlistDistance, timeToDistanceSubsection, (point) => point[1]);

    if (distanceIndex < 0) {
        return null;
    }

    const distanceItem = timeToDistanceSubsection[distanceIndex];

    if (distanceIndex === timeToDistanceSubsection.length - 1) {
        return distanceItem[0];
    }

    const nextDistanceItem = timeToDistanceSubsection[distanceIndex + 1];
    const totalTimeDiff = Math.abs(nextDistanceItem[0] - distanceItem[0]);
    const totalDistanceDiff = nextDistanceItem[1] - distanceItem[1];

    const interpolatedDistanceDiff = playlistDistance - distanceItem[1];
    const ratioDiff = interpolatedDistanceDiff / totalDistanceDiff;
    const interpolatedPlaylistTimestamp = totalTimeDiff * ratioDiff + distanceItem[0];

    return interpolatedPlaylistTimestamp;
}

export function coordinateToPlaylistDistance(coords, geometryCoordinatePositions) {
    const allDistances = geometryCoordinatePositions.flatMap((geomSegment) => {
        return geomSegment.map((segment, idx) => {
            const currentPoint = geomSegment[idx];
            const p1 = [currentPoint[1], currentPoint[2]];

            const nextPoint = geomSegment[idx + 1];

            if (!nextPoint) {
                const distanceToPoint = distance(coords, p1);

                return {
                    distanceToPoint,
                    playlistDistance: geomSegment[0],
                };
            }
            const p2 = [nextPoint[1], nextPoint[2]];
            const nearestPoint = nearestPointOnLine(coords, p1, p2);
            const distanceToNearest = distance(coords, nearestPoint);

            const totalDistanceBetweenPoints = distance(p1, p2);
            const distanceFromFirst = distance(coords, p1);
            const interpolationRatio = distanceFromFirst / totalDistanceBetweenPoints;

            const startPlaylistDistance = currentPoint[0];
            const endPlaylistDistance = nextPoint[0];

            const playlistDistanceDiff = endPlaylistDistance - startPlaylistDistance;
            const playlistDistance = startPlaylistDistance + playlistDistanceDiff * interpolationRatio;

            return {
                distanceToPoint: distanceToNearest,
                playlistDistance,
            };
        });
    });

    const sortedDistances = _.sortBy(allDistances, ["distanceToPoint"], ["asc"]);
    if (sortedDistances.length) {
        return sortedDistances[0].playlistDistance;
    }

    return null;
}

export function coordinateToTimestampV2(coords, geometryData) {
    const geomCoordData = _.get(geometryData, ["distance_to_coordinate"], []);

    const playlistDistance = coordinateToPlaylistDistance(coords, geomCoordData);
    const timestamp = playlistDistanceToTimestamp(playlistDistance, geometryData);

    return timestamp;
}
